/**
 * Formulario "Registrar salida/entrada" (007 · T035; contracts/ui.md §1.4, research R-3, R-9 y
 * R-15, spec US1-4…US1-7, US1-10, US1-11 y US4-4).
 *
 * Pantalla completa sin pestañas (`/regularizar/$markId`, no bottom sheet: dos campos con teclado
 * en iOS). Lee el ítem del ancla de `GET /attendance/regularizations` y **congela** esa foto al
 * abrir: una recarga posterior (foco, invalidación tras enviar) no desmonta el formulario ni la
 * hoja de error. Si el ancla no está o `can_request = false`, "Esta marca ya no se puede
 * regularizar".
 *
 * - Resumen de la marca existente ("Entrada 08:57 · Angamos · martes 23") y, si fue pedida, la
 *   nota del administrador.
 * - Selector de día **solo** si `bounds.allowed_days` trae dos (turno que cruza la medianoche):
 *   segmentado con `aria-pressed`, el día contiguo marcado "Día siguiente"/"Día anterior".
 * - Hora `<input type="time" step="60">` (24 h) con la ayuda "Entre las … y las …" en
 *   `bounds.time_zone`; la validación final es del servidor y sus mensajes van **por campo**.
 * - Motivo obligatorio con contador (`REGULARIZATION_REASON_MAX`); vacío ⇒ "Cuéntanos qué pasó."
 *   sin llamar a la API.
 * - Clave de idempotencia por intento: nace al abrir, se **conserva** en los reintentos del mismo
 *   contenido (un reintento tras un fallo de red no duplica, US1-11) y se **renueva** si cambió el
 *   día, la hora o el motivo.
 * - Sin optimistic UI. Éxito ⇒ `regularize-success` y regreso a Asistencia a los 2,5 s o al tocar.
 *   `REGULARIZATION_UNAVAILABLE`/`NOT_OPEN`/`NOT_FOUND` ⇒ hoja con el texto por motivo y "Volver a
 *   mi asistencia" (las consultas ya se invalidaron en `useCreateRegularization`).
 * - Sin red: "Sin conexión" deshabilitado.
 */
import { useNavigate, useRouter } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import {
  REGULARIZATION_REASON_MAX,
  type StaffProposalBounds,
  type StaffRegularizationItem,
} from '@fermin/contracts';

import { ApiError, NetworkError } from '../../api/client';
import { useOnlineStatus } from '../../api/online';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import { Field } from '../../components/ui/field';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { Input } from '../../components/ui/input';
import { OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Sheet } from '../../components/ui/sheet';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { t } from '../../i18n';

import {
  fieldErrorsOf,
  isStaleStateError,
  newIdempotencyKey,
  unavailableMessage,
  useCreateRegularization,
  useStaffRegularizations,
  type RegularizationFieldErrors,
} from './api';
import { anchorSummary, boundsHelp, defaultProposalDay, shortDayTitle } from './format';

/** Regreso automático a Asistencia tras el éxito (mismo tiempo que la marcación de la 006). */
export const REGULARIZE_SUCCESS_RETURN_MS = 2_500;

function useGoToHistory(): () => void {
  const navigate = useNavigate();
  return () => {
    void navigate({ to: '/asistencia', replace: true });
  };
}

export function RegularizeLoading() {
  return (
    <Screen data-testid="regularize-loading">
      <SkeletonText label={t('common.states.loading')} className="gap-fm-4">
        <Skeleton radius="lg" className="h-20 w-full" />
        <Skeleton radius="lg" className="h-24 w-full" />
        <Skeleton radius="lg" className="h-32 w-full" />
      </SkeletonText>
    </Screen>
  );
}

function RegularizeGone() {
  const goToHistory = useGoToHistory();
  return (
    <Screen
      data-testid="regularize-gone"
      align="center"
      footer={
        <Button variant="secondary" onClick={goToHistory}>
          {t('regularization.gone.back')}
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-fm-3 text-center">
        <span
          aria-hidden="true"
          className="flex h-16 w-16 items-center justify-center rounded-fm-pill bg-fm-warning-soft text-fm-warning-text"
        >
          <CircleAlert size={28} />
        </span>
        <h1 className="font-display text-fm-xl font-bold text-fm-text">{t('regularization.gone.title')}</h1>
        <p className="text-fm-base text-fm-text-secondary">{t('regularization.gone.description')}</p>
      </div>
    </Screen>
  );
}

function RegularizeSuccess() {
  const navigate = useNavigate();
  const goToHistory = useGoToHistory();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void navigate({ to: '/asistencia', replace: true });
    }, REGULARIZE_SUCCESS_RETURN_MS);
    return () => window.clearTimeout(timer);
  }, [navigate]);

  return (
    // Tocar cualquier parte adelanta el regreso; el botón del pie hace lo mismo con teclado.
    <div data-testid="regularize-success" onClick={goToHistory}>
      <Screen
        align="center"
        footer={
          <Button variant="secondary" onClick={goToHistory}>
            {t('regularization.success.back')}
          </Button>
        }
      >
        <div role="status" className="flex flex-col items-center gap-fm-3 text-center">
          <span
            aria-hidden="true"
            className="flex h-24 w-24 items-center justify-center rounded-fm-pill bg-fm-success-soft"
          >
            <svg viewBox="0 0 48 48" className="h-12 w-12">
              <path
                d="M12 25 L21 34 L37 16"
                fill="none"
                strokeWidth={5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-fm-success-text"
              />
            </svg>
          </span>
          <h1 className="font-display text-fm-xl font-bold text-fm-text">{t('regularization.success.title')}</h1>
          <p className="text-fm-base text-fm-text-secondary">{t('regularization.success.description')}</p>
        </div>
      </Screen>
    </div>
  );
}

interface DaySelectorProps {
  item: StaffRegularizationItem;
  bounds: StaffProposalBounds;
  value: string;
  onChange: (day: string) => void;
  error: string | undefined;
}

/** Segmentado de dos días (turno nocturno); el contiguo al ancla dice "Día siguiente/anterior". */
function DaySelector({ item, bounds, value, onChange, error }: DaySelectorProps) {
  const anchorDay = defaultProposalDay(item, bounds);
  const errorId = 'regularize-day-error';
  return (
    <div className="flex flex-col gap-fm-2">
      <span id="regularize-day-label" className="text-fm-base font-medium text-fm-text">
        {t('regularization.form.dayLabel')}
      </span>
      <div
        role="group"
        aria-labelledby="regularize-day-label"
        aria-describedby={error ? errorId : undefined}
        className="grid grid-cols-2 gap-fm-2"
      >
        {bounds.allowed_days.map((day) => {
          const pressed = day === value;
          const hint =
            day === anchorDay
              ? null
              : day > anchorDay
                ? t('regularization.form.nextDay')
                : t('regularization.form.previousDay');
          return (
            <button
              key={day}
              type="button"
              data-testid={`regularize-day-${day}`}
              aria-pressed={pressed}
              onClick={() => onChange(day)}
              className={cn(
                'flex min-h-12 flex-col items-center justify-center rounded-fm-md border px-fm-3 py-fm-2',
                'font-ui text-fm-base transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
                pressed
                  ? 'border-fm-text bg-fm-text font-semibold text-fm-text-inverse'
                  : 'border-fm-border bg-fm-surface text-fm-text',
              )}
            >
              <span>{shortDayTitle(day)}</span>
              {hint !== null ? <span className="text-fm-sm">{hint}</span> : null}
            </button>
          );
        })}
      </div>
      {error ? (
        <p
          id={errorId}
          aria-live="polite"
          className="flex items-center gap-fm-1 text-fm-sm text-fm-danger-text"
        >
          <CircleAlert size={16} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface RegularizeFormProps {
  item: StaffRegularizationItem;
  bounds: StaffProposalBounds;
  onSuccess: () => void;
}

function contentSignature(day: string, time: string, reason: string): string {
  return JSON.stringify([day, time, reason]);
}

function RegularizeForm({ item, bounds, onSuccess }: RegularizeFormProps) {
  const online = useOnlineStatus();
  const router = useRouter();
  const goToHistory = useGoToHistory();
  const create = useCreateRegularization();

  const [day, setDay] = useState(() => defaultProposalDay(item, bounds));
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<RegularizationFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  /** Clave del intento en curso y el contenido con el que se usó por última vez (R-9). */
  const attempt = useRef<{ key: string; signature: string | null }>({
    key: newIdempotencyKey(),
    signature: null,
  });

  const showDaySelector = bounds.allowed_days.length === 2;

  const clearFieldError = (field: keyof RegularizationFieldErrors) => {
    setFieldErrors((current) => {
      if (current[field] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const goBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      goToHistory();
    }
  };

  const handleError = (error: unknown) => {
    if (error instanceof NetworkError) {
      setFormError(t('regularization.form.networkError'));
      return;
    }
    if (!(error instanceof ApiError)) {
      setFormError(t('regularization.form.genericError'));
      return;
    }
    if (isStaleStateError(error)) {
      setUnavailable(unavailableMessage(error));
      return;
    }
    const fields = fieldErrorsOf(error);
    if (fields !== null) {
      setFieldErrors(fields);
      return;
    }
    if (error.is('RATE_LIMITED')) {
      setFormError(t('regularization.form.rateLimited'));
      return;
    }
    if (error.is('CONFLICT')) {
      // `IDEMPOTENCY_KEY_REUSED`: el próximo envío sale con una clave nueva.
      attempt.current = { key: newIdempotencyKey(), signature: null };
    }
    setFormError(t('regularization.form.genericError'));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending || !online) {
      return;
    }
    const trimmed = reason.trim();
    const localErrors: RegularizationFieldErrors = {};
    if (time === '') {
      localErrors.time = t('regularization.form.timeRequired');
    }
    if (trimmed === '') {
      localErrors.reason = t('regularization.form.reasonRequired');
    }
    setFormError(null);
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }
    setFieldErrors({});

    const signature = contentSignature(day, time, trimmed);
    if (attempt.current.signature !== null && attempt.current.signature !== signature) {
      attempt.current = { key: newIdempotencyKey(), signature: null };
    }
    attempt.current.signature = signature;

    create.mutate(
      {
        anchor_mark_id: item.anchor.mark_id,
        date: day,
        time,
        reason: trimmed,
        idempotency_key: attempt.current.key,
      },
      { onSuccess, onError: handleError },
    );
  };

  const timeError = fieldErrors.time ?? (showDaySelector ? undefined : fieldErrors.date);
  const askedNote = item.state === 'ASKED' ? item.request?.ask_note : null;

  return (
    <form noValidate onSubmit={submit} className="contents">
      <Screen
        data-testid="regularize-screen"
        data-missing={item.missing_type}
        title={t(`regularization.form.title.${item.missing_type}`)}
        footer={
          <>
            <Button
              type="submit"
              data-testid="regularize-submit"
              disabled={!online || create.isPending}
              loading={create.isPending}
              loadingText={t('regularization.form.submitting')}
            >
              {online ? t('regularization.form.submit') : t('regularization.form.offline')}
            </Button>
            <Button type="button" variant="ghost" onClick={goBack} disabled={create.isPending}>
              {t('regularization.form.cancel')}
            </Button>
          </>
        }
      >
        <section
          data-testid="regularize-anchor"
          className="flex flex-col gap-fm-1 rounded-fm-lg border border-fm-border bg-fm-surface p-fm-4"
        >
          <span className="text-fm-sm text-fm-text-secondary">{t('regularization.form.existing')}</span>
          <span className="font-ui text-fm-base font-semibold text-fm-text">
            {anchorSummary(item.anchor, true)}
          </span>
          {askedNote ? (
            <span data-testid="regularize-asked-note" className="text-fm-sm text-fm-text-secondary">
              {t('regularization.form.askedNote', { note: askedNote })}
            </span>
          ) : null}
        </section>

        {showDaySelector ? (
          <DaySelector
            item={item}
            bounds={bounds}
            value={day}
            onChange={(next) => {
              setDay(next);
              clearFieldError('date');
            }}
            error={fieldErrors.date}
          />
        ) : null}

        <Field
          label={t(`regularization.form.timeLabel.${item.missing_type}`)}
          help={boundsHelp(bounds)}
          error={timeError}
          required
        >
          {(controlProps) => (
            <Input
              {...controlProps}
              data-testid="regularize-time"
              type="time"
              step={60}
              value={time}
              invalid={timeError !== undefined}
              onChange={(event) => {
                setTime(event.target.value);
                clearFieldError('time');
                clearFieldError('date');
              }}
            />
          )}
        </Field>

        <Textarea
          data-testid="regularize-reason"
          counterTestId="regularize-reason-counter"
          label={t('regularization.form.reasonLabel')}
          placeholder={t('regularization.form.reasonPlaceholder')}
          value={reason}
          maxLength={REGULARIZATION_REASON_MAX}
          error={fieldErrors.reason}
          required
          rows={3}
          onChange={(event) => {
            setReason(event.target.value);
            clearFieldError('reason');
          }}
        />

        {formError !== null ? (
          <p
            data-testid="regularize-error"
            role="alert"
            className="flex items-center gap-fm-2 rounded-fm-md bg-fm-danger-soft p-fm-3 text-fm-base text-fm-danger-text"
          >
            <CircleAlert size={20} aria-hidden="true" className="shrink-0" />
            {formError}
          </p>
        ) : null}
      </Screen>

      <Sheet
        open={unavailable !== null}
        onClose={goToHistory}
        title={t('regularization.unavailable.title')}
        description={unavailable ?? undefined}
        className="pb-fm-4"
        footer={
          <div data-testid="regularize-unavailable" className="flex flex-col gap-fm-3">
            <Button variant="secondary" onClick={goToHistory}>
              {t('regularization.unavailable.back')}
            </Button>
          </div>
        }
      />
    </form>
  );
}

export interface RegularizeScreenProps {
  markId: string;
}

export function RegularizeScreen({ markId }: RegularizeScreenProps) {
  const online = useOnlineStatus();
  const regularizations = useStaffRegularizations();
  const [done, setDone] = useState(false);
  /** Foto del ítem al abrir: las recargas posteriores no desmontan el formulario. */
  const [snapshot, setSnapshot] = useState<StaffRegularizationItem | null>(null);

  const current = regularizations.data?.items.find((entry) => entry.anchor.mark_id === markId);
  const openable = current !== undefined && current.can_request && current.bounds !== null;

  // Estado derivado durante el render (patrón de React para "guardar el primer valor"): sin efecto.
  if (snapshot === null && openable) {
    setSnapshot(current);
  }

  if (done) {
    return <RegularizeSuccess />;
  }

  const item = snapshot ?? (openable ? current : null);
  if (item !== null && item.bounds !== null) {
    return <RegularizeForm item={item} bounds={item.bounds} onSuccess={() => setDone(true)} />;
  }

  if (regularizations.data === undefined) {
    if (!online) {
      return (
        <Screen>
          <OfflineState
            onRetry={() => void regularizations.refetch()}
            retrying={regularizations.isFetching}
          />
        </Screen>
      );
    }
    if (regularizations.isError) {
      return <GenericErrorScreen onRetry={() => void regularizations.refetch()} />;
    }
    return <RegularizeLoading />;
  }

  return <RegularizeGone />;
}
